import { CC, CCTypes } from "@bigspring/core-components";
import { useAnalytics } from "components/shared/Analytics";
import { useAuth } from "components/shared/Authentication";
import { useState } from "react";
import {
  AuthenticationSource,
  useAuthInitMutation,
  useAuthVerifyMutation,
  useGoogleAuthVerifyMutation,
  useAuthChannelLazyQuery,
  useIsNewAuthLazyQuery,
} from "@gql/generated/graphql";
import {
  usePostAuthRequestOtp,
  usePostAuthLogin,
} from "@rest/generated/auth/auth/auth";
import { AxiosError } from "axios";
import { usePostAuthGoogle } from "@rest/generated/auth/oauth/oauth";
import { FlagsmithFeatureType } from "@config/flags";
import { useRouter } from "next/navigation";
import { storeCompanyId, parseAxiosError } from "utils/auth";
import { getRuntimeConfig } from "lib/runtimeEnvClient";

export function Login() {
  const { replace: nextReplace } = useRouter();
  const { login } = useAuth();
  const { client } = useAnalytics();

  const [authInitId, setAuthInitId] = useState("");

  const [authInit] = useAuthInitMutation();
  const { mutateAsync: authInitRequest } = usePostAuthRequestOtp();
  const [authVerify] = useAuthVerifyMutation();
  const { mutateAsync: authVerifyRequest } = usePostAuthLogin();
  const [googleAuthVerifyFn] = useGoogleAuthVerifyMutation();
  const { mutateAsync: googlePostAuth } = usePostAuthGoogle();
  const [getIsNewAuth, { data: newAuthData }] = useIsNewAuthLazyQuery();
  const [authChannel] = useAuthChannelLazyQuery();

  const onSendVerificationCode = async (profileIdentifier: string) => {
    try {
      const isNewAuthResp = await getIsNewAuth({
        variables: { email: profileIdentifier },
      });
      const isNewAuth = isNewAuthResp.data?.isNewAuth;

      if (isNewAuth) {
        console.log("debug using new auth");
        const results = await authInitRequest({
          data: { profileIdentifier: profileIdentifier },
        }).catch((e) => {
          throw new Error(
            e?.response?.data?.message ||
              e.message ||
              "Unable to init authentication"
          );
        });

        if (!results?.profileIdentifier) {
          throw new Error("Unable to init authentication");
        }

        setAuthInitId(results.profileIdentifier);
      } else {
        const results = await authInit({
          variables: {
            profileIdentifier,
          },
        });

        if (!results?.data?.authInit) {
          throw new Error("Unable to init authentication");
        }

        setAuthInitId(results.data.authInit);
      }
    } catch (e) {
      const { code, message } = (e || {}) as any;
      console.info({ e });
      throw { code, message };
    }
  };

  const onSubmitVerificationCode = async (verificationCode: string) => {
    try {
      if (newAuthData?.isNewAuth) {
        const authData = await authVerifyRequest({
          data: {
            profileIdentifier: authInitId,
            otp: verificationCode,
            source: "EDASH",
          },
        }).catch((e) => {
          throw new Error(
            e?.response?.data?.message ||
              e.message ||
              "Unable to init authentication"
          );
        });

        if (authData?.sessionId) {
          sessionStorage.setItem(
            FlagsmithFeatureType.NEW_AUTH,
            newAuthData?.isNewAuth?.toString()
          );
        }

        if ((authData as any)?.mfaRequired) {
          return { mfaRequired: true };
        }

        await login();
      } else {
        const results = await authVerify({
          variables: {
            authInitId,
            code: verificationCode,
          },
        });

        if ((results?.data?.authVerify as any)?.mfaRequired) {
          return { mfaRequired: true };
        }

        await login();
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        throw parseAxiosError(e);
      }

      const { code, message } = (e || {}) as any;
      console.info({ e });
      throw { code, message };
    }
  };

  const onGAuthSuccess = async (credential: string, email: string) => {
    try {
      const isNewAuthResp = await getIsNewAuth({
        variables: { email: email },
      });
      const isNewAuth = isNewAuthResp.data?.isNewAuth;

      if (isNewAuth) {
        console.log("debug using new auth");
        const authData = await googlePostAuth({
          data: { credential: credential, source: "EDASH" },
        }).catch((e) => {
          throw new Error(
            e?.response?.data?.message ||
              e.message ||
              "Unable to init authentication"
          );
        });

        if (authData?.sessionId) {
          sessionStorage.setItem(
            FlagsmithFeatureType.NEW_AUTH,
            isNewAuth?.toString()
          );
        }
      } else {
        await googleAuthVerifyFn({
          variables: {
            idToken: credential,
            source: AuthenticationSource.Admin,
          },
        });
      }

      await login();
    } catch (e) {
      const { code, message } = (e || {}) as any;
      console.error({ e });
      throw { code, message };
    }
  };

  const onSsoSignin = async (profileId: string) => {
    const isNewAuthResp = await getIsNewAuth({
      variables: { email: profileId },
    });
    const isNewAuth = isNewAuthResp.data?.isNewAuth;

    if (isNewAuth) {
      const profileIdEmail = encodeURIComponent(profileId);
      const authDomainUrl = getRuntimeConfig("NEXT_PUBLIC_AUTH_DOMAIN_URL");

      window.location.href = `${authDomainUrl}/sso/init/EDASH?email=${profileIdEmail}`;
    } else {
      try {
        const { data } = await authChannel({
          variables: { profileIdentifier: profileId },
        });
        const chType = data?.authChannel?.channelType;
        const companyId = data?.authChannel?.companyId;
        const companyProfileId = data?.authChannel?.companyProfileId;

        if (!chType || chType !== "SSO") {
          throw new Error(
            "SSO is not enabled for this account. Please request a login code."
          );
        }

        if (!companyId || !companyProfileId) {
          throw new Error("Company profile not found for this email");
        }
        storeCompanyId(companyId);

        const base = data?.authChannel?.ssoChannel?.idpProxyBaseUrl;
        const path = data?.authChannel?.ssoChannel?.idpProxyPath;
        const url = new URL(`${base}${path}`);
        url.searchParams.append("username", profileId);

        nextReplace(url.toString());
      } catch (e) {
        const { code, message } = (e || {}) as any;
        console.error({ e });
        throw { code, message };
      }
    }
  };

  return (
    <CC.Login
      googleApiKey={String(getRuntimeConfig("NEXT_PUBLIC_GOOGLE_API_KEY"))}
      googleMapsApiKey={String(
        getRuntimeConfig("NEXT_PUBLIC_GOOGLE_MAPS_API_KEY")
      )}
      onSendVerificationCode={onSendVerificationCode}
      onSubmitVerificationCode={onSubmitVerificationCode}
      onSsoLogin={onSsoSignin}
      segment={client as CCTypes.SegmentPropFunctions}
      source="ADMIN"
      isSsoEnabled={true}
      onGoogleAuthSuccess={onGAuthSuccess}
    />
  );
}
